import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Gauge, MapPin, User, Search, FileSpreadsheet, Wrench, Upload } from "lucide-react";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { VehicleForm } from "../components/VehicleForm";
import { StatusDot } from "../components/AlertBadge";
import { vidangeLevel } from "../lib/alerts";

export default function VehicleList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user && (user.role === "admin" || user.role === "editor");
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/vehicles");
    setVehicles(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (params.get("nouveau")) {
      setShowForm(true);
      params.delete("nouveau");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const create = async (payload) => {
    await api.post("/vehicles", payload);
    await load();
  };

  const exportExcel = async () => {    try {
      const res = await fetch(`${API}/vehicles/export/excel`, { credentials: "include" });
      if (!res.ok) throw new Error("excel");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "flotte_mobicarsvsd.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch {
      toast.error("Échec de l'export Excel");
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/vehicles/import", fd);
      toast.success(`${data.imported} véhicule(s) importé(s), ${data.skipped} ignoré(s)`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'import Excel");
    }
  };

  const filtered = vehicles.filter(
    (v) =>
      v.immatriculation.toLowerCase().includes(query.toLowerCase()) ||
      (v.conducteur || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="ml-64 p-8 min-h-screen">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 mc-slide-up">
        <div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Véhicules</h1>
          <p className="text-slate-500 font-medium mt-1">{vehicles.length} véhicule(s) dans la flotte</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} strokeWidth={2.5} />
            <input
              data-testid="vehicle-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher..."
              className="nb-input pl-10 pr-4 py-2.5 font-medium text-sm w-56"
            />
          </div>
          <button
            data-testid="export-excel-button"
            onClick={exportExcel}
            className="flex items-center gap-2 bg-[#4ADE80] font-bold px-5 py-2.5 rounded-xl nb-btn"
          >
            <FileSpreadsheet strokeWidth={2.5} size={20} /> Export Excel
          </button>
          {canWrite && (
            <>
              <a
                data-testid="import-template-link"
                href={`${API}/vehicles/import/template`}
                className="flex items-center gap-2 bg-white font-bold px-4 py-2.5 rounded-xl nb-btn text-sm"
              >
                <FileSpreadsheet strokeWidth={2.5} size={18} /> Modèle
              </a>
              <label
                data-testid="import-excel-button"
                className="flex items-center gap-2 bg-[#FBBF24] font-bold px-5 py-2.5 rounded-xl nb-btn cursor-pointer"
              >
                <Upload strokeWidth={2.5} size={20} /> Import Excel
                <input
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  data-testid="import-excel-input"
                  onChange={importExcel}
                />
              </label>
            </>
          )}
          {canWrite && (
            <button
              data-testid="add-vehicle-button"
              onClick={() => setShowForm(true)}
              className="flex items-center gap-2 bg-[#4F46E5] text-white font-bold px-5 py-2.5 rounded-xl nb-btn"
            >
              <Plus strokeWidth={2.5} size={20} /> Ajouter
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-slate-400 font-medium py-16 text-center">Chargement...</p>
      ) : filtered.length === 0 ? (
        <div className="nb-card p-12 text-center" data-testid="empty-state">
          <p className="font-heading font-black text-2xl mb-2">Aucun véhicule</p>
          <p className="text-slate-500 font-medium mb-6">Ajoutez votre premier véhicule pour commencer le suivi.</p>
          <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-[#4F46E5] text-white font-bold px-6 py-3 rounded-xl nb-btn">
            <Plus strokeWidth={2.5} size={20} /> Ajouter un véhicule
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filtered.map((v, i) => {
            const vLevel = vidangeLevel(v.vidange_km_restant);
            return (
              <div
                key={v.id}
                data-testid={`vehicle-card-${i}`}
                onClick={() => navigate(`/vehicules/${v.id}`)}
                className={`nb-card p-5 cursor-pointer transition-all hover:-translate-y-1 hover:nb-shadow-lg mc-pop ${v.en_reparation ? "ring-4 ring-[#FB923C]" : ""}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono-plate font-bold bg-white border-2 border-slate-900 rounded px-3 py-1.5 text-sm uppercase relative pl-7">
                    <span className="absolute left-0 top-0 bottom-0 w-3.5 bg-[#4F46E5] rounded-l" />
                    {v.immatriculation}
                  </span>
                  {v.en_reparation ? (
                    <span data-testid={`repair-badge-${i}`} className="flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-lg border-2 border-slate-900 bg-[#FB923C] text-slate-900">
                      <Wrench size={13} strokeWidth={2.5} /> Réparation
                    </span>
                  ) : (
                    <span
                      className={`text-xs font-bold px-2.5 py-1 rounded-lg border-2 border-slate-900 ${
                        v.statut === "indisponible" ? "bg-[#EF4444] text-white" : "bg-[#4ADE80] text-slate-900"
                      }`}
                    >
                      {v.statut === "indisponible" ? "Indispo" : "Dispo"}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-3 text-sm font-semibold">
                  <User size={16} strokeWidth={2.5} className="text-slate-400" />
                  {v.conducteur || "Sans conducteur"}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                    <Gauge size={16} strokeWidth={2.5} className="text-slate-400" />
                    {(v.kilometrage || 0).toLocaleString("fr-FR")} km
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-600 font-medium">
                    <MapPin size={16} strokeWidth={2.5} className="text-slate-400" />
                    {v.geoloc_equipe ? v.geoloc_position || "GPS équipé" : "Non équipé"}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-4 border-t-2 border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">CT 6m</span>
                    <StatusDot dateStr={v.controle_6mois_date} />
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-1">CT an</span>
                    <StatusDot dateStr={v.controle_annuel_date} />
                  </div>
                  <span
                    className={`text-xs font-bold px-2 py-1 rounded-lg border-2 border-slate-900 ${
                      vLevel === "green" ? "bg-[#4ADE80]" : vLevel === "orange" ? "bg-[#FB923C]" : vLevel === "red" ? "bg-[#EF4444] text-white" : "bg-slate-100"
                    }`}
                  >
                    Vidange {v.vidange_km_restant?.toLocaleString("fr-FR")} km
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && <VehicleForm onSubmit={create} onClose={() => setShowForm(false)} />}
    </div>
  );
}
